import {
  ApiOutlined,
  AutoFixHighOutlined,
  DataObjectOutlined,
  DifferenceOutlined,
  PrecisionManufacturingOutlined,
  RouteOutlined,
  SpeedOutlined,
} from '@mui/icons-material';
import { Box, Button, Container, Grid, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useRef } from 'react';

import FeatureSlider from '../components/FeatureSlider';

const capabilities = [
  { icon: <DataObjectOutlined />, text: 'Compatible with commercial FEA meshes (Ansys, Siemens NX, etc.).' },
  { icon: <SpeedOutlined />, text: 'Handles large surface scans (100M+ points).' },
  { icon: <PrecisionManufacturingOutlined />, text: 'Processes dense models with 10M+ nodes effortlessly.' },
  { icon: <RouteOutlined />, text: 'Fully automated and robust part registration.' },
  { icon: <AutoFixHighOutlined />, text: 'Journaling and scripting for workflow automation.' },
  { icon: <ApiOutlined />, text: 'Comprehensive APIs for integration.' },
  { icon: <DifferenceOutlined />, text: 'Advanced Boolean operations for precise geometry edits.' },
];

const products = [
  {
    name: 'FEMORPH GUI',
    description: 'Interactive environment for data inspection, alignment, and mesh morphing workflows.',
    image: '/use-cases/gui_shiny.png',
    imageAlt: 'FEMORPH GUI interface',
  },
  {
    name: 'FEMORPH Blender',
    description: 'Surface and edge blend repair for damaged or out-of-tolerance geometry.',
    image: '/case_studies/blend-repair.webp',
    imageAlt: 'FEMORPH Blender blend repair',
  },
  {
    name: 'FEMORPH CADmorph',
    description: 'Geometric feature-directed morphing driven directly by CAD surface definitions.',
    image: '/features/thumbnails/nist-cad.webp',
    imageAlt: 'FEMORPH CADmorph CAD-driven morphing',
  },
  {
    name: 'FEMORPH Automesher',
    description: 'Tetrahedral mesh generation directly from measurement data or CAD geometry.',
    image: '/features/thumbnails/mean-to-scan-6.webp',
    imageAlt: 'FEMORPH Automesher tetrahedral mesh',
  },
];

export default function FeaturesPage() {
  const featureSliderRef = useRef<HTMLDivElement | null>(null);

  const handleScrollToFeatures = () => {
    featureSliderRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box sx={{ bgcolor: 'background.default' }}>

      {/* Hero */}
      <Box component="section" sx={{ bgcolor: '#172126', color: 'white', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="xl" sx={{ py: { xs: 7, md: 10 } }}>
          <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">

            {/* Left: core capability */}
            <Grid item xs={12} md={5}>
              <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                Mesh morphing platform
              </Typography>
              <Typography variant="h2" sx={{ mt: 1, mb: 2, fontSize: { xs: '2.4rem', md: '3.4rem' }, lineHeight: 1.1 }}>
                Target Surface Morphing
              </Typography>
              <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.78)', fontWeight: 400, lineHeight: 1.65, mb: 4 }}>
                FEMORPH's core engine conforms existing finite element meshes to any target surface — whether sourced from CAD geometry or physical measurements — without rebuilding the mesh from scratch.
              </Typography>
              <Button variant="contained" size="large" onClick={handleScrollToFeatures}>
                Explore capabilities
              </Button>
            </Grid>

            {/* Right: product grid */}
            <Grid item xs={12} md={7}>
              <Grid container spacing={1.5}>
                {products.map((product) => (
                  <Grid item xs={12} sm={6} key={product.name}>
                    <Box
                      sx={{
                        bgcolor: alpha('#fff', 0.05),
                        border: '1px solid',
                        borderColor: alpha('#fff', 0.12),
                        overflow: 'hidden',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      {/* Thumbnail */}
                      <Box
                        component="img"
                        src={product.image}
                        alt={product.imageAlt}
                        sx={{
                          width: '100%',
                          height: 140,
                          objectFit: 'cover',
                          objectPosition: 'center',
                          borderBottom: '1px solid',
                          borderColor: alpha('#fff', 0.1),
                        }}
                      />
                      {/* Text */}
                      <Box sx={{ p: 2 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ color: 'secondary.light', fontWeight: 700, letterSpacing: '0.04em', mb: 0.5 }}
                        >
                          {product.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>
                          {product.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>

          </Grid>
        </Container>
      </Box>

      {/* Capabilities bar */}
      <Box component="section" sx={{ bgcolor: 'background.paper' }}>
        <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 } }}>
          <Grid container spacing={2.5}>
            {capabilities.map((capability) => (
              <Grid item xs={12} sm={6} lg={3} key={capability.text}>
                <Stack
                  direction="row"
                  spacing={1.5}
                  sx={{
                    height: '100%',
                    p: 2.25,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    alignItems: 'flex-start',
                  }}
                >
                  <Box sx={{ color: 'primary.main', mt: 0.2, '& svg': { fontSize: 24 } }}>{capability.icon}</Box>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {capability.text}
                  </Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box ref={featureSliderRef}>
        <FeatureSlider />
      </Box>
    </Box>
  );
}
